import { useState, useEffect } from 'react';

interface BookPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'lt' | 'en';
}

const previewPagesLT = [
  {
    title: 'Įvadas',
    content: `Sveiki atvykę į kelionę per keturis vartus – Ugnies, Oro, Vandens ir Žemės. Ši knyga nėra tik apie astrologiją. Ji apie tai, kaip suprasti save giliau, nei kada nors anksčiau.

Kiekvienas iš mūsų gimsta su unikalia energijų kombinacija. Kai kurios energijos mums ateina natūraliai, kitos reikalauja sąmoningo darbo. Bet svarbiausia – visos jos yra mūsų dalis.

Per šią knygą išmoksite atpažinti, kurios energijos jums šiuo metu trūksta, ir kaip ją subalansuoti per paprastus, kasdienius veiksmus.`
  },
  {
    title: '1 Skyrius: Ugnies Vartai',
    content: `Ugnis – tai veikimo, drąsos ir aistrų energija. Tai ta jėga, kuri stumia mus į priekį, verčia rizikuoti ir siekti savo tikslų.

Žmonės su stipria Ugnies energija yra:
• Drąsūs ir ryžtingi
• Kupini entuziazmo
• Natūralūs lyderiai
• Greiti priimti sprendimus

Kai Ugnies energijos trūksta, galite jausti:
• Motyvacijos stoką
• Baimę pradėti naujus projektus
• Energijos trūkumą
• Sunkumą ginti savo nuomonę`
  },
  {
    title: '1 Skyrius: Ugnies Vartai (tęsinys)',
    content: `Kaip sustiprinti Ugnies energiją:

1. Rytinė praktika (15 min)
Pradėkite dieną nuo fizinio aktyvumo – tai gali būti trumpas bėgimas, šokiai ar net energingas pasivaikščiojimas.

2. Drąsos iššūkis
Kasdien padarykite vieną dalyką, kuris jus šiek tiek gąsdina. Tai gali būti pokalbis su nepažįstamu žmogumi ar naujo hobio išbandymas.

3. Raudonos spalvos meditacija
Įsivaizduokite, kaip raudona šviesa užpildo jūsų kūną, suteikdama jėgų ir drąsos.`
  }
];

const previewPagesEN = [
  {
    title: 'Introduction',
    content: `Welcome to the journey through the four gates – Fire, Air, Water, and Earth. This book is not just about astrology. It's about understanding yourself deeper than ever before.

Each of us is born with a unique combination of energies. Some energies come naturally to us, others require conscious work. But most importantly – they are all part of us.

Through this book, you will learn to recognize which energy you currently lack and how to balance it through simple, everyday actions.`
  },
  {
    title: 'Chapter 1: The Fire Gate',
    content: `Fire is the energy of action, courage, and passion. It's the force that pushes us forward, makes us take risks, and pursue our goals.

People with strong Fire energy are:
• Brave and determined
• Full of enthusiasm
• Natural leaders
• Quick to make decisions

When Fire energy is lacking, you may feel:
• Lack of motivation
• Fear of starting new projects
• Energy deficiency
• Difficulty defending your opinion`
  },
  {
    title: 'Chapter 1: The Fire Gate (continued)',
    content: `How to strengthen Fire energy:

1. Morning Practice (15 min)
Start your day with physical activity – it can be a short run, dancing, or even an energetic walk.

2. Courage Challenge
Every day, do one thing that slightly scares you. It could be talking to a stranger or trying a new hobby.

3. Red Color Meditation
Visualize red light filling your body, giving you strength and courage.`
  }
];

export default function BookPreview({ isOpen, onClose, language }: BookPreviewProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev'>('next');

  const previewPages = language === 'lt' ? previewPagesLT : previewPagesEN;
  const texts = {
    lt: {
      preview: 'Knygos Peržiūra',
      page: 'Puslapis',
      of: 'iš',
      prev: 'Ankstesnis',
      next: 'Kitas',
      close: 'Uždaryti',
      buyFull: 'Įsigyti Pilną Knygą',
      previewNote: '📖 Tai tik trumpa ištrauka. Pilnoje knygoje rasite giliausias įžvalgas, asmeninius pavyzdžius, detalesnius pratimus ir transformuojančias istorijas.'
    },
    en: {
      preview: 'Book Preview',
      page: 'Page',
      of: 'of',
      prev: 'Previous',
      next: 'Next',
      close: 'Close',
      buyFull: 'Buy Full Book',
      previewNote: '📖 This is just a brief excerpt. The full book contains deeper insights, personal examples, detailed exercises, and transformative stories.'
    }
  };
  const t = texts[language];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentPage(0);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const goToPage = (direction: 'next' | 'prev') => {
    if (isFlipping) return;
    
    if (direction === 'next' && currentPage < previewPages.length - 1) {
      setFlipDirection('next');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(prev => prev + 1);
        setIsFlipping(false);
      }, 400);
    } else if (direction === 'prev' && currentPage > 0) {
      setFlipDirection('prev');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(prev => prev - 1);
        setIsFlipping(false);
      }, 400);
    }
  };

  const scrollToPurchase = () => {
    onClose();
    setTimeout(() => {
      const element = document.getElementById('purchase');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl mx-4 bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900 rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/20 rounded-full">
              <i className="ri-book-open-line text-amber-400 text-xl"></i>
            </div>
            <h2 className="text-white font-serif text-xl font-bold">{t.preview}</h2>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Preview Notice */}
        <div className="mx-6 mt-4 px-4 py-3 bg-amber-400/10 border border-amber-400/20 rounded-lg">
          <p className="text-amber-100 text-sm leading-relaxed">
            {t.previewNote}
          </p>
        </div>

        {/* Book Content */}
        <div className="relative px-6 py-8 min-h-[500px]">
          {/* Page */}
          <div 
            className={`bg-amber-50 rounded-lg shadow-xl mx-auto max-w-2xl transition-all duration-400 ${
              isFlipping 
                ? flipDirection === 'next' 
                  ? 'animate-flipNext' 
                  : 'animate-flipPrev'
                : ''
            }`}
            style={{ perspective: '1000px' }}
          >
            {/* Page content */}
            <div className="p-8 md:p-12">
              <h3 className="font-serif text-2xl md:text-3xl text-gray-800 font-bold mb-6 pb-4 border-b-2 border-amber-200">
                {previewPages[currentPage].title}
              </h3>
              <div className="text-gray-700 text-base md:text-lg leading-relaxed whitespace-pre-line font-serif">
                {previewPages[currentPage].content}
              </div>
            </div>
            
            {/* Page number */}
            <div className="text-center pb-6 text-gray-500 text-sm">
              {t.page} {currentPage + 1} {t.of} {previewPages.length}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={() => goToPage('prev')}
            disabled={currentPage === 0 || isFlipping}
            className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              currentPage === 0 
                ? 'bg-white/5 text-white/20 cursor-not-allowed' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          
          <button
            onClick={() => goToPage('next')}
            disabled={currentPage === previewPages.length - 1 || isFlipping}
            className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              currentPage === previewPages.length - 1 
                ? 'bg-white/5 text-white/20 cursor-not-allowed' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pb-2">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-300"
              style={{ width: `${((currentPage + 1) / previewPages.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-white/10">
          {/* Page dots */}
          <div className="flex items-center gap-2">
            {previewPages.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isFlipping && index !== currentPage) {
                    setFlipDirection(index > currentPage ? 'next' : 'prev');
                    setIsFlipping(true);
                    setTimeout(() => {
                      setCurrentPage(index);
                      setIsFlipping(false);
                    }, 400);
                  }
                }}
                className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                  index === currentPage 
                    ? 'bg-amber-400 w-6' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
          
          {/* Buy button */}
          <button
            onClick={scrollToPurchase}
            className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 px-6 py-2.5 rounded-full font-semibold hover:from-amber-500 hover:to-amber-600 transition-all shadow-lg flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-cart-line"></i>
            {t.buyFull}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes flipNext {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(-15deg); }
          100% { transform: rotateY(0deg); }
        }
        @keyframes flipPrev {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(15deg); }
          100% { transform: rotateY(0deg); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-flipNext {
          animation: flipNext 0.4s ease-in-out;
        }
        .animate-flipPrev {
          animation: flipPrev 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
}
